import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {RichTextField} from "../../../common/newRichText";
import type {VariableCatalogue} from "../../../common/newVariablePicker";
import {surveyPickerVariables, type SurveyVariable} from "../surveyVariables";

interface Props {
    value?: LocalizedText
    language: string
    onChange: (value: LocalizedText | undefined) => void
    /** `title` and `heading` are one line; `heading` is a section title above the question, one size up. */
    variant: 'title' | 'heading' | 'description'
    placeholder: string
    variables: SurveyVariable[]
    disabled?: boolean
    /** A focus request, handed to the field's `focusKey`. */
    focusKey?: number
}

/**
 * A question text in the language being edited: the shared rich text field (`common/newRichText`, its AGENTS.md is
 * the contract) with the survey's variables and the canvas's text sizes. Every survey text offers alignment and
 * emoji; descriptions add lists.
 */
function SurveyMarkdownField({value, language, onChange, variant, placeholder, variables, disabled, focusKey}: Props) {
    const {t} = useTranslation();

    // The samples are one language, but the preview still names the field's: never silently German.
    const catalogue = useMemo<VariableCatalogue>(
        () => ({variables: surveyPickerVariables(variables, t, language), preview: {language}}),
        [variables, t, language],
    );

    // An emptied language goes from the text, and a text without any language is no text.
    const write = (markdown: string) => {
        const next: LocalizedText = {...value};
        if (markdown) {
            next[language] = markdown;
        } else {
            delete next[language];
        }

        onChange(Object.keys(next).length ? next : undefined);
    };

    return (
        <RichTextField
            value={value?.[language] ?? ''}
            onChange={write}
            placeholder={placeholder}
            disabled={disabled}
            singleLine={variant !== 'description'}
            textSize={variant === 'description' ? 'body' : variant}
            alignment
            emoji
            variables={catalogue}
            focusKey={focusKey}
        />
    );
}

export default SurveyMarkdownField;
